<script lang="ts">
    import {api} from "src/services/api/reallis"
    import {validateEmail, navigate} from "src/components/main/#common/helpers"
    import MainLayout from "../#common/layout/Layout.svelte"
    import {Link} from 'svelte-routing'
    import IconifyIcon from '@iconify/svelte'
    import facebookIcon from '@iconify/icons-fa-brands/facebook'
    import googleIcon from '@iconify/icons-fa-brands/google'
    import hideIcon from '@iconify/icons-fa-solid/eye'
    import showIcon from '@iconify/icons-fa-solid/eye-slash'
    import uIcon from 'src/components/#assets/u.svg'
    import spirale from 'src/components/#assets/spirale.png'
    import elipse from 'src/components/#assets/elipse.png'
    import realisId from 'src/components/main/#assets/realis-id.svg'
    import Alert from 'src/components/main/#common/alert/Alert.svelte'
    import {showAlert} from "src/components/main/#common/alert/store"
    import FacebookLogin from "svelte-facebook-login"
    import 'src/components/main/#common/oauth/google/oauthGoogleCallback.ts'
    import {isLoggedIn} from 'src/services/auth'
    import {sendCredential} from 'src/components/main/#services/socialAuth'
    import {removeAccount} from 'src/components/main/#services/account'
    import {deleteTwoFactorConfirm} from 'src/components/cabinet/#services/twoFactorAuthentication'

    export let hash = null
    export let twoFaHash = null

    let email = ''
    let password = ''
    let errorStatus = false
    let errorText = ''
    let rememberMe = false
    let sending = false
    let showPassword = false

    async function deleteAccount() {
        try {
            api.ws.ws1.auth.logout()

            await removeAccount.request({ data: { hash } })

            showAlert.set({
                status: 'success',
                text: 'Your account has been successfully deleted'
            })
        } catch (err) {
            if (err.message) {
                showAlert.set({
                    status: 'error',
                    text: err.message
                })
            } else {
                showAlert.set({
                    status: 'error',
                    text: 'An Error Occurred, Please Try Again Later'
                })
            }
        } finally {
            navigate("/login")
        }
    }

    async function delete2fa() {
        try {
            api.ws.ws1.auth.logout()

            await deleteTwoFactorConfirm.request({ data: { hash: twoFaHash } })

            showAlert.set({
                status: 'success',
                text: '2FA successfully deleted'
            })

            navigate("/login")
        } catch (err) {
            if (err.message) {
                showAlert.set({
                    status: 'error',
                    text: err.message
                })
            } else {
                showAlert.set({
                    status: 'error',
                    text: 'An Error Occurred, Please Try Again Later'
                })
            }
            navigate("/login")
        }
    }

    if (hash) {
        deleteAccount()
    }

    if (twoFaHash) {
        delete2fa()
    }

    async function facebookAuthSuccess(params) {
        try {
            await sendCredential.request({
                data: {
                    provider  : 'facebook',
                    credential: params.detail.code,
                },
            })
        } catch (err) {
            console.error(err)
            if (err.message.includes('ban')) {
                showAlert.set({
                    status: 'error',
                    text: 'Your Account Has Been Banned',
                })
            } else {
                showAlert.set({
                    status: 'error',
                    text: 'Facebook authorization error',
                })
            }
        }
    }

    function facebookAuthError(error) {
        showAlert.set({
            status: 'error',
            text  : error.detail.message,
        })
    }

    async function login() {
        if (!email) {
            errorStatus = true
            errorText = 'Enter your Email'
            return
        }

        const _email = email
        if (!validateEmail(email)) {
            errorStatus = true
            errorText = 'Please enter a valid Email'
            return
        }

        if (password == '') {
            errorStatus = true;
            errorText = 'Enter your Password'
            return
        }

        try {
            sending = true
            errorStatus = false;

            await api.ws.ws1.login({
                username: _email,
                password: password,
                sessionTime: rememberMe ? 0 : 24 * 60 * 60 * 1000 , //  0 (disabled) or 24 hours
            })

            email = ''
            password = ''
        } catch (err) {
            if (err.message == 'Account disabled') {
                showAlert.set({
                    status: 'message',
                    text: 'You have not confirmed Email. Confirmation email has been sent again.'
                })
            } else if (err.code === 'user-banned') {
                navigate('/ban')
                return
            } else if (err.message == 'User with this email doesn\'t exist') {
                showAlert.set({
                    status: 'error',
                    text: 'You have entered an invalid email or password'
                })
            } else {
                showAlert.set({
                    status: 'error',
                    text: err.message
                })
            }
        } finally {
            sending = false
        }
    }

    function showHidePassword() {
        showPassword = !showPassword
    }

    $: if ($isLoggedIn && !hash && !twoFaHash) {
        navigate("/account")
    }
</script>

<svelte:head>
    <title>Login</title>
    <script src="https://apis.google.com/js/platform.js" async defer></script>
    <script src="https://accounts.google.com/gsi/client" async defer></script>
</svelte:head>

<MainLayout>
    <svelte:fragment slot="content">
        <div class="panel-wrapper">
            <div class="container">
                <div class="auth-background"></div>
                <div class="auth-wrapper">
                    <img src="{elipse}" class="elipse" alt="">
                    <img src="{spirale}" class="spirale" alt="">
                    <img src="{uIcon}" class="u-icon" alt="">
                    <img src="{realisId}" class="realis-id" alt="realis id">
                    <Alert />
                    <div class="auth-social-buttons">
                        <div id="g_id_onload"
                             data-client_id="492902765781-hf4ck2a3v69vh2vhvjjleul09tvposgm.apps.googleusercontent.com"
                             data-callback="oauthGoogleCallback">
                        </div>
                        <div class="g_id_signin"
                             data-type="standard"
                             data-size="large"
                             data-theme="fill_black"
                             data-text="sign_in_with"
                             data-shape="rectangular"
                             data-logo_alignment="left"
                             data-width="416"
                             data-height="56">
                        </div>
                        <div class="auth-social-btn auth-google-btn">
                            <div class="auth-social-icon">
                                <IconifyIcon icon={googleIcon}/>
                            </div>
                            <div class="auth-social-text">Sign in with Google</div>
                        </div>
                        <FacebookLogin
                            clientId="424456532882971"
                            state="1"
                            scope="email"
                            redirectUri={process.env.FACEBOOK_REDIRECT_URL}
                            on:success={params => facebookAuthSuccess(params)}
                            on:error={error => facebookAuthError(error)}
                            let:onLogin>
                            <div class="auth-social-btn auth-facebook-btn" on:click={onLogin}>
                                <div class="auth-social-icon">
                                    <IconifyIcon icon={facebookIcon} class="social-icon"/>
                                </div>
                                <div class="auth-social-text">Sign in with Facebook</div>
                            </div>
                        </FacebookLogin>
                    </div>
                    <div class="or"><span>or</span></div>
                    <form on:submit|preventDefault={login} novalidate>
                        <div class="input-group">
                            <input type="email" id="EmailInput" class="form-control field" placeholder="Your Email address"
                               autocomplete="email" required bind:value={email}>
                        </div>
                        <div class="input-group">
                            {#if showPassword === true}
                                <input type="text" class="form-control field" bind:value={password} placeholder="Your Password" autocomplete="password">
                            {:else}
                                <input type="password" class="form-control field" bind:value={password} placeholder="Your Password" autocomplete="password">
                            {/if}
                            <button type="button" class="show-hide-password" on:click={showHidePassword}><IconifyIcon icon={showPassword ? showIcon : hideIcon}/></button>
                        </div>
                        <div class="text-center">
                            <div class="invalid-feedback">Please enter a valid email.</div>
                        </div>
                        <!--
                        <div class="input-group">
                            <label for="remember" class="remember">
                                <input class="form-check-input" type="checkbox" id="remember" bind:checked={rememberMe}>Remember me
                            </label>
                        </div>
                        -->
                        <div class="text-center">
                            <div class="invalid-feedback {errorStatus ? 'd-block' : ''}">{errorText}</div>
                            <button type="submit" class="btn-login input-group-text btn btn-primary auth-button {sending ? 'loading' : ''}"
                                disabled={sending}><span>Log in</span></button>
                        </div>
                        <div class="not-have-account"><Link to="forgotten">Forgot password?</Link></div>
                        <hr>
                        <div class="not-have-account">Don't have an account? <Link to="register">Create account</Link></div>
                        <div class="auth-form-terms">
                            By using, you agree to the <Link to="terms" class="link">Terms of Use</Link>,
                            <Link to="policy" class="link">Privacy Policy</Link>, the processing and storage of cookies
                            and agree to the newsletter from Realis.
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </svelte:fragment>
</MainLayout>

<style>
    .container {
        position: relative;
    }

    .auth-form-terms {
        font-size: 13px;
        line-height: 19px;
        color: #A0AEC0;
        margin: 30px 0 -5px;
        font-weight: 200;
    }

    .realis-id {
        margin: 0 auto 35px;
        display: block;
        width: 80px;
        height: 106px;
    }

    .auth-background {
        background: linear-gradient(220deg, rgba(44, 43, 64, 0.15) 23%, #1E1920 50%);
        filter: blur(200px);
        width: calc(100% - 24px);
        height: 550px;
        position: absolute;
    }

    .auth-wrapper {
        background: #27232C;
        border-radius: 10px;
        border: 2px solid #7B7DB3;
        max-width: 480px;
        margin: 60px auto 150px;
        padding: 35px 40px;
        z-index: 2;
        position: relative;
    }

    .auth-social-buttons {
        margin-bottom: 20px;
        text-decoration: none;
        position: relative;
        transition: .25s;
    }

    .auth-social-btn {
        text-decoration: none;
        display: flex;
        color: #fff;
        height: 56px;
        align-items: center;
        justify-content: center;
        border-radius: 3px;
        position: relative;
        max-width: 550px;
        border: 1px solid #B8BAFD;
        margin: 0 auto 20px;
        text-align: center;
        transition: .25s;
        cursor: pointer;
    }

    .auth-social-btn:hover {
        background: rgb(54 46 60);
    }

    .g_id_signin:hover + .auth-google-btn {
        background: rgb(54 46 60);
    }

    .auth-wrapper .form-control {
        border: 1px solid #7B7DB3;
        border-radius: 3px;
        box-shadow: none !important;
    }

    .show-hide-password {
        position: absolute;
        background: none;
        right: 1px;
        top: 6px;
        height: 48px;
        width: 48px;
        color: #939599;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 0;
        z-index: 3;
    }

    .auth-social-text {
        font-size: 18px;
    }

    .auth-social-icon {
        font-size: 28px;
        margin-right: 10px;
    }

    .auth-form-terms :global(.link) {
        color: #A0AEC0;
        transition: .25s;
    }

    .auth-form-terms :global(.link):hover {
        color: white;
    }

    .after-login-form :global(.link) {
        color: gray;
        text-decoration: none;
        transition: .25s;
    }

    .after-login-form :global(.link):hover {
        color: #adadad;
    }

    .or {
        margin: 28px auto;
        max-width: 550px;
        font-size: 18px;
        line-height: 27px;
        color: #A0AEC0;
        text-align: center;
        position: relative;
    }

    .or span {
        padding: 0 10px;
        background: #27232C;
        position: relative;
        z-index: 2;
    }

    .or:before {
        content: "";
        height: 2px;
        width: 100%;
        background: rgba(160, 174, 192, 0.3);
        position: absolute;
        left: 0;
        top: 50%;
        margin-top: -1px;
    }

    .auth-button {
        width: 100%;
        margin-top: 20px;
        padding: 17px 45px !important;
        font-weight: 500;
    }

    .auth-wrapper :global(.alert) {
        font-size: 14px;
        margin-bottom: 20px;
        text-align: left;
        padding: 15px 45px 15px 15px;
    }

    .input-group {
        max-width: 550px;
        margin: 10px auto;
    }

    label {
        color: white;
        font-size: 16px;
        line-height: 28px;
        position: relative;
        cursor: pointer;
        font-weight: 300;
        user-select: none;
    }

    hr {
        height: 2px;
        background: #4A4A55;
    }

    input[type="checkbox"] {
        font-size: 20px;
        position: relative;
        cursor: pointer;
    }

    input {
        margin: 5px 0;
    }

    .btn.btn-primary {
        margin: 5px auto 20px;
    }

    .remember input {
        margin-right: 10px;
    }

    .not-have-account {
        font-size: 14px;
        color: white;
    }

    .not-have-account :global(a) {
        color: #2F80ED;
        font-weight: 500;
        text-decoration: none;
        border-bottom: 1px solid #2F80ED;
    }

    .elipse {
        position: absolute;
        z-index: 2;
        left: -289px;
        top: -63px;
        -webkit-user-drag: none;
    }

    .spirale {
        position: absolute;
        z-index: 2;
        right: -320px;
        top: 330px;
        -webkit-user-drag: none;
    }

    .u-icon {
        position: absolute;
        z-index: 2;
        top: 574px;
        left: -180px;
        -webkit-user-drag: none;
    }

    .g_id_signin {
        position: absolute;
        width: 100%;
        height: 56px;
        z-index: 2;
        opacity: 0;
        cursor: pointer;
        overflow: hidden;
    }

    .g_id_signin :global [role="button"] {
        height: 56px;
    }

    .g_id_signin :global iframe {
        margin: 0 !important;
        width: 100% !important;
        height: 56px !important;
    }

    @media(max-width: 1400px) {
        .spirale {
            right: -250px;
        }
    }

    @media(max-width: 991px) {
        .spirale {
            right: -130px;
        }

        .auth-social-text {
            font-size: 16px;
        }

        .auth-social-icon {
            font-size: 26px;
        }

        .auth-social-btn {
            height: 48px;
        }

        .auth-wrapper .form-control {
            padding: 11px 24px;
        }

        .show-hide-password {
            top: 1px;
            height: 38px;
            width: 38px;
        }

        .g_id_signin {
            height: 48px;
        }

        .g_id_signin :global [role="button"] {
            height: 48px;
        }

        .g_id_signin :global iframe {
            height: 48px !important;
        }
    }

    @media(max-width: 767px) {
        .spirale,
        .elipse,
        .u-icon {
            display: none;
        }

        .auth-wrapper {
            margin: 20px auto -20px;
            padding: 30px;
        }

        .auth-social-btn {
            height: 40px;
            margin: 0 auto 10px;
        }

        .auth-social-text {
            font-size: 15px;
        }

        .auth-social-icon {
            font-size: 20px;
        }

        .or {
            font-size: 18px;
            margin: 20px auto;
        }

        .auth-wrapper .form-control {
            padding: 8px 20px;
            font-size: 16px;
            margin: 0;
        }

        .auth-button {
            padding: 12px 15px !important;
            margin-top: 10px;
        }

        .g_id_signin {
            height: 40px;
        }

        .g_id_signin :global [role="button"] {
            height: 40px;
        }

        .g_id_signin :global iframe {
            height: 40px !important;
        }

        .auth-form-terms {
            font-size: 12px;
            line-height: 17px;
            margin: 20px 0 0;
        }

        .realis-id {
            margin: 0 auto 30px;
            width: 54px;
            height: auto;
        }
    }
</style>
